import {
  ComponentProps,
  createContext,
  MouseEventHandler,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Box, FelaCSS, Text } from '@bridebook/ui';
import { ms } from '@bridebook/ui/src/fela-utils/ms';
import { IconChevronFat } from '@bridebook/ui/src/icons';
import { useIsElementScrolled } from '@bridebook/ui/src/utils/hooks/use-is-element-scrolled';
import Modal from '@bridebook/web/components/bbcommon/modal';
import PopupRounded from '@bridebook/web/components/generic/popup-rounded/popup-rounded';
import { getScreenType } from '@bridebook/web/lib/app/selectors';
import { useSelector } from '@bridebook/web/lib/utils';
import styles from './full-mobile-modal-v2.style';
import { Icon } from '@bridebook/aura';
import { Close } from '@bridebook/aura/src/icons';

type ModalContextType = {
  onClose?: () => void;
  isContentScrolled?: boolean;
  setIsContentScrolled?: React.Dispatch<React.SetStateAction<boolean>>;
};

const ModalContext = createContext<ModalContextType>({});

export const useModalContext = () => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModalContext must be used within a ModalContextProvider');
  }
  return context;
};

const ModalContextProvider = ({
  children,
  onClose,
}: {
  children: React.ReactNode;
  onClose?: () => void;
}) => {
  const [isContentScrolled, setIsContentScrolled] = useState(false);

  return (
    <ModalContext.Provider value={{ isContentScrolled, setIsContentScrolled, onClose }}>
      {children}
    </ModalContext.Provider>
  );
};

interface IProps {
  show: boolean;
  onClose?: () => void;
  id: string;
  children: React.ReactNode;
  header?: string | React.ReactNode;
  modalProps?: Omit<ComponentProps<typeof Modal>, 'show' | 'id' | 'onClose'>;
  popupRoundedProps?: Omit<
    ComponentProps<typeof PopupRounded>,
    'show' | 'id' | 'onClose' | 'children'
  >;
  styles?: {
    mobileWrapper?: FelaCSS;
  };
}

/**
 * On mobile, displays as a full-screen modal with a sticky header.
 * On desktop, displays as a rounded popup with a sticky X button.
 *
 * This is a wrapper. It is ment to be used with FullMobileModalV2.Header, FullMobileModalV2.Content and FullMobileModalV2.Footer. Search for usage in the codebase or check the example below.
 * But can be used without - to customize the modal even more.
 * Footer and header are on the top and bottom so if there is scroll, it will only be inside content.
 * When scrolled, the header will have a bottom border.
 *
 * @example
 * <FullMobileModalV2
 *   show={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   id="example-modal"
 *   header="Modal Title"
 * >
 *   <FullMobileModalV2.Header desktopVariant="bottom-border">
 *     Modal header goes here
 *   </FullMobileModalV2.Header>
 *   <FullMobileModalV2.Content>
 *     Modal content goes here
 *   </FullMobileModalV2.Content>
 *   <FullMobileModalV2.Footer style={{ background: 'red' }}>
 *     Footer content
 *   </FullMobileModalV2.Footer>
 * </FullMobileModalV2>
 */

export const FullMobileModalV2 = ({
  show,
  onClose,
  id,
  children,
  modalProps,
  popupRoundedProps,
  styles: propsStyles,
}: IProps) => {
  const { isDesktop } = useSelector(getScreenType);

  if (!isDesktop) {
    return (
      <Modal show={show} id={id} onClose={onClose} maxWidth="100%" {...modalProps}>
        <ModalContextProvider onClose={onClose}>
          <Box style={ms(styles.mobile.wrapper, propsStyles?.mobileWrapper)}>{children}</Box>
        </ModalContextProvider>
      </Modal>
    );
  }

  return (
    <PopupRounded
      id={id}
      show={show}
      onClose={onClose}
      {...popupRoundedProps}
      modalStyle={ms(styles.desktop.modalStyle, popupRoundedProps?.modalStyle)}
      buttonStyle={ms(styles.desktop.buttonStyle, popupRoundedProps?.buttonStyle)}>
      <ModalContextProvider onClose={onClose}>{children}</ModalContextProvider>
    </PopupRounded>
  );
};

const Header = ({
  children,
  onBack,
  styles: propsStyles,
}: {
  children: React.ReactNode;
  onBack?: MouseEventHandler<ReactNode>;
  styles?: { mobileHeader?: FelaCSS; desktopHeader?: FelaCSS };
}) => {
  const { isDesktop } = useSelector(getScreenType);
  const { onClose, isContentScrolled } = useModalContext();

  // Mobile and tablet
  if (!isDesktop) {
    return (
      <Box style={ms(styles.mobile.header, propsStyles?.mobileHeader)}>
        {onBack && (
          <Box style={styles.mobile.backIcon} onClick={onBack}>
            <IconChevronFat width={12} />
          </Box>
        )}
        <>
          {typeof children === 'string' ? (
            <Box style={styles.mobile.title}>{children}</Box>
          ) : (
            children
          )}
        </>
        {onClose && (
          <Box style={styles.mobile.closeIcon} onClick={onClose}>
            <Icon icon={Close} size={20} />
          </Box>
        )}
      </Box>
    );
  }

  // Desktop
  return (
    <>
      {typeof children === 'string' ? (
        <Box
          style={ms(
            styles.desktop.header,
            isContentScrolled && styles.desktop.headerBottomBorder,
            propsStyles?.desktopHeader,
          )}>
          {onBack && (
            <Box style={styles.desktop.backIcon} onClick={onBack}>
              <IconChevronFat width={12} />
            </Box>
          )}
          <Text style={styles.desktop.titleText}>{children}</Text>
        </Box>
      ) : (
        children
      )}
    </>
  );
};

FullMobileModalV2.Header = Header;

const Content = ({
  children,
  styles: propsStyles,
  id,
}: {
  children: React.ReactNode;
  styles?: { content?: FelaCSS; wrapper?: FelaCSS };
  id?: string;
}) => {
  const { setIsContentScrolled } = useModalContext();
  const ref = useRef<HTMLDivElement>(null);
  const { isScrolled: isContentScrolledFromHook } = useIsElementScrolled(ref);

  useEffect(() => {
    setIsContentScrolled?.(isContentScrolledFromHook);
  }, [isContentScrolledFromHook, setIsContentScrolled]);

  return (
    <Box
      style={ms(styles.contentWrapper, propsStyles?.wrapper)}
      setRef={ref}
      id={id ?? 'content-wrapper'}>
      <Box style={ms(styles.content, propsStyles?.content)}>{children}</Box>
    </Box>
  );
};

FullMobileModalV2.Content = Content;

const Footer = ({ children, style }: { children: React.ReactNode; style?: FelaCSS }) => (
  <Box style={ms(styles.footer, style)}>{children}</Box>
);

FullMobileModalV2.Footer = Footer;
